import { ONE_MINUTE_MS } from 'constants/misc'
import { useQuery, UseQueryOptions } from 'react-query'
import { useAccount } from 'wagmi'

interface CoinGeckoPriceResponse {
  [tokenId: string]: {
    usd: number
  }
}

type ReactQueryOptions = Pick<
  UseQueryOptions<number, Error>,
  'staleTime' | 'cacheTime' | 'retry' | 'retryDelay' | 'refetchOnWindowFocus' | 'refetchOnMount' | 'refetchInterval'
>

interface UseNativeTokenPriceOptions extends ReactQueryOptions {
  tokenId?: string
  shouldFetch?: boolean
}

const DEFAULT_TOKEN_ID = 'ducatus'
const DEFAULT_STALE_TIME = 5 * ONE_MINUTE_MS
const DEFAULT_CACHE_TIME = 30 * ONE_MINUTE_MS

async function fetchTokenPrice(tokenId: string): Promise<number> {
  const url = `https://api.coingecko.com/api/v3/simple/price?ids=${tokenId}&vs_currencies=usd`
  const response = await fetch(url)

  if (!response.ok) {
    throw new Error(`CoinGecko API error: ${response.status}`)
  }

  const data: CoinGeckoPriceResponse = await response.json()
  return data[tokenId]?.usd ?? 0
}

export function useNativeTokenPrice(options: UseNativeTokenPriceOptions = {}) {
  const { tokenId = DEFAULT_TOKEN_ID, shouldFetch = true, ...queryOptions } = options

  const { address: account, chainId } = useAccount()

  const {
    data: priceUsd,
    isLoading,
    error,
    refetch,
  } = useQuery<number, Error>(['nativeTokenPrice', tokenId], () => fetchTokenPrice(tokenId), {
    enabled: Boolean(account && chainId && shouldFetch),
    staleTime: DEFAULT_STALE_TIME,
    cacheTime: DEFAULT_CACHE_TIME,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(2000 * 2 ** attemptIndex, 30000),
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
    ...queryOptions,
  })

  return {
    priceUsd: priceUsd ?? 0,
    isLoading,
    error,
    refetch,
  }
}
